import { AsyncState, unloadedAsyncState, Fun, Updater } from 'widgets-for-react'
import { loadBlogTeasers } from '../blogs/blogTeasers/blogTeasersApi'
import { BlogTeasersState } from '../blogs/blogTeasers/blogTeasersState'
import { loadCollections } from '../collections/collectionsLoader/collectionsLoaderApi'
import { CollectionsLoaderState } from '../collections/collectionsLoader/collectionsLoaderState'
import {
  ComponentSaverState,
  initialComponentSaverState,
  componentSaverStateUpdaters,
} from '../components/componentSaver/componentSaverState'
import { loadComponentTeasers } from '../components/componentTeasers/componentTeasersApi'
import { ComponentTeasersState } from '../components/componentTeasers/componentTeasersState'
import { loadDishTeasers } from '../dishes/dishTeasers/dishTeasersApi'
import { DishTeasersState } from '../dishes/dishTeasers/dishTeasersState'
import { loadKnowledgeTeasers } from '../knowledge/knowledgeTeasers/knowledgeTeasersApi'
import { KnowledgeTeasersState } from '../knowledge/knowledgeTeasers/knowledgeTeasersState'
import {
  PaginationState,
  initialPaginationState,
} from '../pagination/paginationState'
import {
  loadSearchCount,
  getSearchResults,
  getSearchCount,
  getComponentSearchResults,
  getComponentSearchCount,
} from './searchApi'
import { searchResultsPerPage } from './searchWidget'
import {
  TopFiltersState,
  initialTopFiltersState,
} from '../filters/topFilters/topFiltersState'
import { TopFilters } from '../filters/filtersUtils'

export const searchTabs = [
  'components',
  'dishes',
  'knowledge',
  'blogs',
  'collections',
] as const

export type SearchTab = (typeof searchTabs)[number]

export type SearchState = {
  searchTabs: {
    [searchTab in SearchTab]: {
      totalCount: AsyncState<number>
      results: searchTab extends 'components'
        ? ComponentTeasersState
        : searchTab extends 'dishes'
        ? DishTeasersState
        : searchTab extends 'knowledge'
        ? KnowledgeTeasersState
        : searchTab extends 'blogs'
        ? BlogTeasersState
        : CollectionsLoaderState
    }
  }
  componentSaverState: ComponentSaverState
  paginationState: PaginationState
  topFiltersState: TopFiltersState
}

export const initialSearchState = (
  searchTab: SearchTab,
  page: number,
  term?: string,
  topFilters?: TopFilters,
  previousTotalCounts?: {
    [searchTab in SearchTab]: AsyncState<number>
  }
): SearchState => {
  return {
    searchTabs: {
      components: {
        totalCount:
          previousTotalCounts?.components.kind === 'loaded'
            ? previousTotalCounts.components
            : loadSearchCount('components', term, topFilters),
        results:
          searchTab === 'components'
            ? loadComponentTeasers(() =>
                getComponentSearchResults(
                  term,
                  searchResultsPerPage('components'),
                  topFilters,
                  page - 1
                )
              )
            : unloadedAsyncState(),
      },
      dishes: {
        totalCount:
          previousTotalCounts?.dishes.kind === 'loaded'
            ? previousTotalCounts.dishes
            : loadSearchCount('dishes', term),
        results:
          searchTab === 'dishes'
            ? loadDishTeasers(() =>
                getSearchResults(
                  'dishes',
                  term,
                  searchResultsPerPage('dishes'),
                  page - 1
                )
              )
            : unloadedAsyncState(),
      },
      knowledge: {
        totalCount:
          previousTotalCounts?.knowledge.kind === 'loaded'
            ? previousTotalCounts.knowledge
            : loadSearchCount('knowledge', term),
        results:
          searchTab === 'knowledge'
            ? loadKnowledgeTeasers(() =>
                getSearchResults(
                  'knowledge',
                  term,
                  searchResultsPerPage('knowledge'),
                  page - 1
                )
              )
            : unloadedAsyncState(),
      },
      blogs: {
        totalCount:
          previousTotalCounts?.blogs.kind === 'loaded'
            ? previousTotalCounts.blogs
            : loadSearchCount('blogs', term),
        results:
          searchTab === 'blogs'
            ? loadBlogTeasers(() =>
                getSearchResults(
                  'blogs',
                  term,
                  searchResultsPerPage('blogs'),
                  page - 1
                )
              )
            : unloadedAsyncState(),
      },
      collections: {
        totalCount:
          previousTotalCounts?.collections.kind === 'loaded'
            ? previousTotalCounts.collections
            : loadSearchCount('collections', term),
        results:
          searchTab === 'collections'
            ? loadCollections(
                () =>
                  getSearchResults(
                    'collections',
                    term,
                    searchResultsPerPage('collections'),
                    page - 1
                  ),
                unloadedAsyncState()
              )
            : unloadedAsyncState(),
      },
    },
    componentSaverState: initialComponentSaverState,
    paginationState: initialPaginationState(
      () =>
        searchTab === 'components'
          ? getComponentSearchCount(term, topFilters)
          : getSearchCount(searchTab, term),
      { tab: searchTab, term: term ?? '' }
    ),
    topFiltersState: initialTopFiltersState(topFilters ?? {}),
  }
}

export const searchStateUpdaters = {
  topFiltersStateUpdater:
    (updater: Updater<TopFiltersState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      topFiltersState: updater(state.topFiltersState),
    }),
  totalCountUpdater:
    (searchTab: SearchTab) =>
    (updater: Updater<AsyncState<number>>) =>
    (state: SearchState): SearchState => ({
      ...state,
      searchTabs: {
        ...state.searchTabs,
        [searchTab]: {
          ...state.searchTabs[searchTab],
          totalCount: updater(state.searchTabs[searchTab].totalCount),
        },
      },
    }),
  componentTeasersStateUpdater:
    (
      updater: Fun<
        ComponentTeasersState,
        ComponentTeasersState & { saveComponentId: number | 'none' }
      >
    ) =>
    (state: SearchState): SearchState => {
      const newState = updater(state.searchTabs.components.results)
      return {
        ...state,
        searchTabs: {
          ...state.searchTabs,
          components: {
            ...state.searchTabs.components,
            results: newState,
          },
        },
        componentSaverState:
          newState.saveComponentId == 'none'
            ? state.componentSaverState
            : componentSaverStateUpdaters.startSavingUpdater(
                newState.saveComponentId
              )(state.componentSaverState),
      }
    },
  dishesTeasersStateUpdater:
    (updater: Updater<DishTeasersState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      searchTabs: {
        ...state.searchTabs,
        dishes: {
          ...state.searchTabs.dishes,
          results: updater(state.searchTabs.dishes.results),
        },
      },
    }),
  knowledgeTeasersStateUpdater:
    (updater: Updater<KnowledgeTeasersState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      searchTabs: {
        ...state.searchTabs,
        knowledge: {
          ...state.searchTabs.knowledge,
          results: updater(state.searchTabs.knowledge.results),
        },
      },
    }),
  blogTeasersStateUpdater:
    (updater: Updater<BlogTeasersState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      searchTabs: {
        ...state.searchTabs,
        blogs: {
          ...state.searchTabs.blogs,
          results: updater(state.searchTabs.blogs.results),
        },
      },
    }),
  collectionTeasersStateUpdater:
    (updater: Updater<CollectionsLoaderState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      searchTabs: {
        ...state.searchTabs,
        collections: {
          ...state.searchTabs.collections,
          results: updater(state.searchTabs.collections.results),
        },
      },
    }),
  componentSaverStateUpdater:
    (
      updater: Fun<
        ComponentSaverState,
        ComponentSaverState & { reloadCollections?: boolean }
      >
    ) =>
    (state: SearchState): SearchState & { reloadCollections?: boolean } => {
      const { reloadCollections, ...newState } = updater(
        state.componentSaverState
      )
      return {
        ...state,
        componentSaverState: newState,
        reloadCollections: reloadCollections,
      }
    },
  paginationStateUpdater:
    (updater: Updater<PaginationState>) =>
    (state: SearchState): SearchState => ({
      ...state,
      paginationState: updater(state.paginationState),
    }),
}
